import { ChangeDetectionStrategy, Component, inject, signal, LOCALE_ID, computed, OnInit, ViewChild } from '@angular/core';
import { RouterLink, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { BundleService } from '../../../../../services/bundle/bundle';
import { BundleFiltersComponent } from './bundle-filters-component/bundle-filters-component';
import { BundleTableComponent } from './bundle-table-component/bundle-table-component';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import {
  BundleListItem,
  BundleListParams,
} from '../../../../../pages/admin-dashboard/promotions/bundles-managment/bundles.model';
import { HasScopeDirective } from '../../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../../services/scope/scope-constants';

@Component({
  selector: 'app-bundles-list-component',
  imports: [
    RouterLink,
    BundleFiltersComponent,
    BundleTableComponent,
    Pagination,
    ConfirmationDialog,
    SuccessfulNotification,
    HasScopeDirective,
  ],
  templateUrl: './bundles-list-component.html',
  styleUrl: './bundles-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BundlesListComponent implements OnInit {
  @ViewChild(BundleFiltersComponent) filtersComponent!: BundleFiltersComponent;
  readonly SCOPES = SCOPES;

  private bundleService = inject(BundleService);
  private locale = inject(LOCALE_ID);
  private router = inject(Router);

  bundles = signal<BundleListItem[]>([]);
  totalItems = signal(0);
  loading = signal(false);
  error = signal<string | null>(null);

  currentPage = signal(1);
  pageSize = signal(25);

  filters = signal<BundleListParams>({
    orderBy: 'START_DATE',
    orderDir: 'DESC',
  });

  deleteModal = signal(false);
  bundleToDelete = signal<BundleListItem | null>(null);
  loadingDelete = signal(false);
  errorDelete = signal('');
  deleteSuccess = signal(false);

  confirmationModal = signal(false);
  activationType = signal<'activate' | 'deactivate' | null>(null);
  bundleToActivate = signal<BundleListItem | null>(null);
  loadingActivation = signal(false);
  errorActivation = signal('');
  activationSuccess = signal(false);

  isArabic = computed(() => this.locale.startsWith('ar'));
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  startItem = computed(() => getStartItem(this.currentPage(), this.pageSize(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.pageSize(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));
  state = computed<LoadState>(() => (this.loading() ? 'loading' : this.error() ? 'error' : 'success'));

  isFilterApplied = computed(() => {
    const f = this.filters();
    return !!(
      f.title?.trim() ||
      f.status?.length ||
      f.discountTypes?.length ||
      f.startDateFrom ||
      f.startDateTo ||
      f.endDateFrom ||
      f.endDateTo
    );
  });

  ngOnInit() {
    this.loadBundles();
  }

  loadBundles() {
    this.loading.set(true);
    this.error.set(null);

    const currentFilters = this.filters();
    const apiParams: BundleListParams = {
      ...currentFilters,
      pageNum: this.currentPage() - 1,
      pageSize: this.pageSize(),
      status: currentFilters.status?.length ? currentFilters.status : undefined,
      discountTypes: currentFilters.discountTypes?.length ? currentFilters.discountTypes : undefined,
    };

    this.bundleService
      .getBundles(apiParams)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => {
          this.bundles.set(res.list);
          this.totalItems.set(res.total);
        },
        error: (err) => {
          // if (err.status === 403) {
          //   this.unAuthorized.set(true);
          //   return;
          // }
          this.error.set(
            this.isArabic() ? err.error?.messageAr || 'حدث خطأ ما' : err.error?.messageEn || 'Something went wrong',
          );
        },
      });
  }

  applyFilters() {
    this.currentPage.set(1);
    this.filters.update((f) => ({ ...f }));
    this.loadBundles();
  }

  resetFilters() {
    this.filtersComponent?.closeAllFiltersDropdowns();
    if (this.filtersComponent) {
      this.filtersComponent.bundleSearchTerm.set('');
    }
    this.filters.set({
      orderBy: 'START_DATE',
      orderDir: 'DESC',
      status: undefined,
      discountTypes: undefined,
      title: undefined,
      startDateFrom: undefined,
      startDateTo: undefined,
      endDateFrom: undefined,
      endDateTo: undefined,
    });
    this.currentPage.set(1);
    this.loadBundles();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadBundles();
    }
  }

  onPageSizeChange(value: number) {
    this.pageSize.set(value);
    this.currentPage.set(1);
    this.loadBundles();
  }

  handleToggleSort() {
    const currentDir = this.filters().orderDir;
    const newDir = currentDir === 'ASC' ? 'DESC' : 'ASC';
    this.filters.update((f) => ({ ...f, orderDir: newDir }));
    this.loadBundles();
  }

  openDeleteModal(id: string) {
    const bundle = this.bundles().find((b) => b.id === id);
    if (!bundle) return;
    this.bundleToDelete.set(bundle);
    this.errorDelete.set('');
    this.deleteModal.set(true);
  }

  closeDeleteModal() {
    this.deleteModal.set(false);
  }

  confirmDelete() {
    const bundle = this.bundleToDelete();
    if (!bundle) return;
    this.loadingDelete.set(true);
    this.bundleService
      .deleteBundle(bundle.id)
      .pipe(finalize(() => this.loadingDelete.set(false)))
      .subscribe({
        next: () => {
          this.deleteModal.set(false);
          this.deleteSuccess.set(true);
          this.loadBundles();
          setTimeout(() => this.deleteSuccess.set(false), 2000);
        },
        error: (err) => {
          const body = err.error || err;
          this.errorDelete.set(
            this.isArabic()
              ? body?.messageAr || 'لا يمكن حذف هذه الباقة'
              : body?.messageEn || 'Cannot delete this bundle',
          );
        },
      });
  }

  openActivationModal(id: string, type: 'activate' | 'deactivate') {
    const bundle = this.bundles().find((b) => b.id === id);
    if (!bundle) return;
    this.bundleToActivate.set(bundle);
    this.activationType.set(type);
    this.errorActivation.set('');
    this.confirmationModal.set(true);
  }

  closeActivationModal() {
    this.confirmationModal.set(false);
  }

  confirmActivation() {
    const bundle = this.bundleToActivate();
    const type = this.activationType();
    if (!bundle || !type) return;
    this.loadingActivation.set(true);
    const action$ =
      type === 'activate'
        ? this.bundleService.activateBundle(bundle.id)
        : this.bundleService.deactivateBundle(bundle.id);
    action$.pipe(finalize(() => this.loadingActivation.set(false))).subscribe({
      next: () => {
        this.confirmationModal.set(false);
        this.activationSuccess.set(true);
        this.loadBundles();
        setTimeout(() => {
          this.activationSuccess.set(false);
          this.activationType.set(null);
        }, 2000);
      },
      error: (err) => {
        const body = err.error || err;
        this.errorActivation.set(
          this.isArabic() ? body?.messageAr || 'حدث خطأ ما' : body?.messageEn || 'Something went wrong',
        );
      },
    });
  }

  navigateToEdit(id: string) {
    this.router.navigate(['/admin/bundles/edit-bundle', id]);
  }

  navigateToView(id: string) {
    this.router.navigate(['/admin/bundles/bundle-details', id]);
  }
}
